<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import useLoading from '../../../../Composables/useLoading.ts'
import useModal from '../../../../Composables/useModal.ts'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'

type FormCreatorType = InstanceType<typeof FormCreator>
type FormDefParameters = {
  entityId?: number
}

const props = defineProps<{
  entityId: number
  address?: Address | null
}>()

const emit = defineEmits<{
  close: []
  save: [{ address: Address }]
}>()

const { t } = useI18n()
const { loading, setLoading } = useLoading(true)
const { loading: sending } = useLoading(false)
const { modal, modalId, open, close } = useModal({ prefix: 'address-' })

const form = ref<FormCreatorType | null>(null)

const modalTitle = computed(() => (props.address ? t('entity.action.editAddress') : t('entity.action.addAddress')))
const defParams = computed<FormDefParameters>(() => (props.address?.id ? {} : { entityId: props.entityId }))

async function submit() {
  sending.value = true
  try {
    await form.value?.methods()
  } finally {
    sending.value = false
  }
}

function submitted(address: Address) {
  emit('save', { address })
  close()
}

function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}

onMounted(() => {
  open()
})
</script>
<template>
  <ModalDialog :id="modalId" ref="modal" :title="modalTitle" class-body="px-3" @icon-close="emit('close')">
    <template #body>
      <FormCreator
        :id="address?.id"
        endpoint="addresses"
        :params="defParams"
        send-params
        src-error="formaddress"
        class-to-add="d-none"
        @form-return="submitted"
        @update-data="onFormLoad"
        @is-loading="setLoading"
      />

      <LazyContent :loading="loading" :class="{ 'p-2': loading }">
        <div v-if="form" class="row">
          <div class="col-12">
            <FieldCreator name="siret" form-ref="formaddress" :form-data="form" domain="address" label-top />
          </div>
          <div class="col-12">
            <FieldCreator name="address" form-ref="formaddress" :form-data="form" domain="address" label-top />
          </div>
          <div class="col-4">
            <FieldCreator name="postalCode" form-ref="formaddress" :form-data="form" domain="address" label-top />
          </div>
          <div class="col-8">
            <FieldCreator name="city" form-ref="formaddress" :form-data="form" domain="address" label-top />
          </div>
          <div class="col-8">
            <FieldCreator
              name="countryCode"
              form-ref="formaddress"
              :form-data="form"
              domain="address"
              label-top
              type-value="string"
            />
          </div>
        </div>
      </LazyContent>
    </template>
    <template v-if="!loading" #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" @click="close" />
        <ButtonAll margin="no" type="action" label="button.confirm" class="ml-2" :loading="sending" @click="submit" />
      </div>
    </template>
  </ModalDialog>
</template>
